import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "./api";
import { supabase } from "./supabase";

const AuthContext = createContext(null);

/**
 * Turn any error-ish input into a readable, non-empty string.
 * Handles: Supabase AuthError, plain strings, numbers (0, HTTP status), objects, Axios errors,
 * FastAPI detail objects/arrays. Falls back to a friendly default so we never render "0"/"{}".
 */
const JUNK_MESSAGES = new Set(["", "0", "{}", "[]", "null", "undefined", "[object Object]"]);

function _cleanString(s) {
  if (typeof s !== "string") return null;
  const t = s.trim();
  return t && !JUNK_MESSAGES.has(t) ? t : null;
}

export function readError(err, fallback = "Something went wrong. Please try again.") {
  if (err == null) return fallback;
  if (typeof err === "string") return _cleanString(err) || fallback;
  if (typeof err === "number" || typeof err === "boolean") return fallback;

  // Axios: err.response.data.detail is common with FastAPI
  const detail = err?.response?.data?.detail;
  if (detail != null) {
    const dStr = _cleanString(detail);
    if (dStr) return dStr;
    if (typeof detail === "object") {
      const nested = _cleanString(detail.message) || _cleanString(detail.msg);
      if (nested) return nested;
      if (Array.isArray(detail) && detail[0]?.msg) {
        const arr = _cleanString(detail[0].msg);
        if (arr) return arr;
      }
    }
  }

  // Supabase AuthError often has __isAuthError = true. If .message is unusable
  // (server-500 unexpected_failure with a JSON-stringified body → "{}"), fall through.
  const messageStr = _cleanString(err.message);
  if (messageStr) return messageStr;
  const descStr = _cleanString(err.error_description) || _cleanString(err.msg) || _cleanString(err.error);
  if (descStr) return descStr;

  // Supabase-specific structured errors
  if (err.code === "unexpected_failure" || err.status === 500) {
    return "The email service is temporarily unavailable. Please try again in a minute.";
  }
  if (err.code === "over_email_send_rate_limit" || err.status === 429) {
    return "Too many attempts. Please try again in a few minutes.";
  }

  const nameStr = _cleanString(err.name);
  if (nameStr && nameStr !== "Error") return nameStr;

  return fallback;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);           // Mongo user (role/access/etc) — null when logged out
  const [pendingApproval, setPendingApproval] = useState(false);
  const [session, setSession] = useState(null);     // Supabase session
  const [loading, setLoading] = useState(true);
  // "View as" impersonation — SA/Admin only. When set, sidebar + dashboards
  // mirror the impersonated user's role/data. Writes are still attributed to
  // the acting SA/Admin (this is Option B from the design discussion).
  const [impersonateUser, setImpersonateUserState] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("gb_impersonate_user") || "null"); }
    catch { return null; }
  });
  const setImpersonateUser = (u) => {
    setImpersonateUserState(u);
    try {
      if (u) sessionStorage.setItem("gb_impersonate_user", JSON.stringify(u));
      else sessionStorage.removeItem("gb_impersonate_user");
    } catch { /* ignore */ }
    // Fire an audit log — dual-attribution: actor is the caller (recorded via
    // the JWT), target is `u`. Non-blocking; failures shouldn't break the UI.
    (async () => {
      try {
        if (u?.id) await api.post("/impersonation/start", { target_user_id: u.id });
        else await api.post("/impersonation/exit", {});
      } catch { /* ignore */ }
    })();
  };

  // While a password-recovery session is in flight (user clicked the reset
  // email link) we must NOT auto-log them in — otherwise the app whisks them
  // to the dashboard before they get a chance to set a new password. This
  // flag is set on the PASSWORD_RECOVERY event and cleared once the recovery
  // flow completes (via `finishPasswordRecovery`).
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  // Guard flag: while we're executing signup() we don't want the AuthProvider to
  // react to the transient "SIGNED_IN" event that Supabase fires the moment it
  // creates the user (even when a confirmation email is required, some project
  // configurations still emit a short-lived session). We sign them out immediately
  // but need to make sure fetchProfile / setSession never run in that gap.
  const signupInFlight = useRef(false);

  const fetchProfile = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      setPendingApproval(null);
      return { profile: r.data, pendingCode: null };
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      const status = err?.response?.status;
      if (status === 401 && code === "EMAIL_NOT_VERIFIED") {
        // Backend rejected — Supabase email_confirmed_at is null. Kill the session
        // so the user can't linger in a half-authenticated state, then let the
        // caller surface the EMAIL_NOT_CONFIRMED code (same UI branch).
        try { await supabase.auth.signOut(); } catch { /* ignore */ }
        setSession(null);
        setUser(null);
        const e = new Error("EMAIL_NOT_CONFIRMED");
        e.code = "EMAIL_NOT_CONFIRMED";
        throw e;
      }
      if (err?.response?.status === 403 && (code === "PENDING_APPROVAL_NEW" || code === "ACCESS_RESTRICTED" || code === "PENDING_APPROVAL")) {
        const normalized = code === "PENDING_APPROVAL" ? "PENDING_APPROVAL_NEW" : code;
        setPendingApproval(normalized);
        setUser(null);
        return { profile: null, pendingCode: normalized };
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    // If the URL contains recovery params (hash fragment from the email link),
    // treat this as a password-recovery flow from the very first render — the
    // ResetPassword page needs to know before supabase-js has finished parsing.
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const search = typeof window !== "undefined" ? window.location.search : "";
    const isRecoveryUrl = /type=recovery/.test(hash) || /type=recovery/.test(search);
    if (isRecoveryUrl) setPasswordRecovery(true);

    supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      // Skip fetchProfile during a recovery flow — the session that arrives
      // with a recovery link is not a normal login and MUST NOT auto-navigate
      // the user into the app.
      if (data.session && !isRecoveryUrl) {
        try { await fetchProfile(); } catch { /* handled */ }
      }
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;
      // Ignore any auth changes fired while a signup is in flight — the signup
      // helper will explicitly sign the user out and never navigate them into the app.
      if (signupInFlight.current) return;
      // PASSWORD_RECOVERY is fired when the user lands on our app via the
      // Supabase reset-password email link. The session that arrives with it
      // is a short-lived recovery session — NOT a normal login — so we must
      // not populate `user` (which would trigger the Protected route to
      // consider them logged in and route them to the dashboard). We simply
      // track the recovery session so the ResetPassword page can read it via
      // `passwordRecovery` and `session`, without disturbing the app state.
      if (event === "PASSWORD_RECOVERY") {
        setPasswordRecovery(true);
        setSession(newSession);
        setUser(null);
        setPendingApproval(false);
        return;
      }
      // While a recovery is in progress, ignore any subsequent SIGNED_IN /
      // TOKEN_REFRESHED events for the same session so the app doesn't
      // silently promote the recovery session into a real login.
      if (passwordRecovery && (event === "SIGNED_IN" || event === "TOKEN_REFRESHED")) {
        setSession(newSession);
        return;
      }
      setSession(newSession);
      if (!newSession) {
        setUser(null);
        setPendingApproval(false);
      } else {
        try { await fetchProfile(); } catch { /* handled */ }
      }
    });
    return () => { mounted = false; sub?.subscription?.unsubscribe?.(); };
  }, [fetchProfile, passwordRecovery]);

  const login = async (email, password) => {
    window.__gbManualLogout = false;
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (error) {
      const raw = readError(error, "Sign in failed. Please try again.");
      const lower = raw.toLowerCase();
      if (lower.includes("email not confirmed") || lower.includes("not confirmed")) {
        const e = new Error("EMAIL_NOT_CONFIRMED");
        e.code = "EMAIL_NOT_CONFIRMED";
        throw e;
      }
      throw new Error(raw);
    }
    setSession(data.session);
    const { profile, pendingCode } = await fetchProfile();
    if (!profile) {
      const err = new Error(pendingCode || "PENDING_APPROVAL_NEW");
      err.code = pendingCode || "PENDING_APPROVAL_NEW";
      throw err;
    }
    // Fire the session-start hook so the backend logs a fresh `login` activity
    // row and bumps today's login counter. Runs after fetchProfile so the JWT
    // is already in the axios default headers. Failures are silent — we don't
    // want a network hiccup here to block sign-in.
    try {
      await api.post("/auth/session-start");
    } catch (_) { /* non-blocking */ }
    return profile;
  };

  const signup = async ({ email, password, name }) => {
    signupInFlight.current = true;
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: { name },
          emailRedirectTo: `${window.location.origin}/verify-email`,
        },
      });
      if (error) {
        const raw = readError(error, "Signup failed. Please try again.");
        const lower = raw.toLowerCase();
        if (lower.includes("already registered") || lower.includes("already been registered") || lower.includes("user already") || lower.includes("already exists")) {
          const e = new Error("EMAIL_ALREADY_REGISTERED");
          e.code = "EMAIL_ALREADY_REGISTERED";
          throw e;
        }
        if (lower.includes("password") && (lower.includes("weak") || lower.includes("short") || lower.includes("characters"))) {
          throw new Error(raw);
        }
        if (lower.includes("invalid") && lower.includes("email")) {
          throw new Error("Please enter a valid email address.");
        }
        if (lower.includes("rate limit") || lower.includes("too many")) {
          throw new Error("Too many attempts. Please try again in a few minutes.");
        }
        throw new Error(raw);
      }

      // Belt & braces: if Supabase returned a session (e.g. confirmations turned
      // off, or a project-specific autoconfirm race), immediately kill it. The
      // client must NEVER be treated as signed-in from a signup call — they must
      // confirm their email and log in fresh.
      if (data?.session) {
        try { await supabase.auth.signOut(); } catch { /* ignore */ }
      }
      setSession(null);
      setUser(null);
      setPendingApproval(false);

      return {
        email: data?.user?.email || email,
        // Even when a transient session was returned, we treat the signup as
        // requiring email confirmation to keep behaviour consistent with the
        // dashboard setting requested by the client.
        needsEmailConfirm: true,
      };
    } finally {
      // Wait one microtask so the ignored onAuthStateChange event has flushed.
      setTimeout(() => { signupInFlight.current = false; }, 0);
    }
  };

  const requestPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw new Error(readError(error, "Could not send the password-reset email."));
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(readError(error, "Could not update the password."));
  };

  const resendConfirmation = async (email) => {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: email.trim().toLowerCase(),
      options: { emailRedirectTo: `${window.location.origin}/verify-email` },
    });
    if (error) throw new Error(readError(error, "Could not resend the confirmation email."));
  };

  const logout = async () => {
    try { window.__gbManualLogout = true; await api.post("/auth/logout"); } catch { /* backend may be unreachable */ }
    try { await supabase.auth.signOut(); } catch { /* ignore */ }
    try { sessionStorage.removeItem("gb_just_signed_in"); } catch { /* private mode */ }
    try { sessionStorage.removeItem("gb_impersonate_user"); } catch { /* private mode */ }
    setImpersonateUserState(null);
    setUser(null);
    setSession(null);
    setPendingApproval(false);
  };

  const refresh = async () => {
    try { return await fetchProfile(); } catch { return null; }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        pendingApproval,
        login,
        signup,
        logout,
        refresh,
        requestPasswordReset,
        updatePassword,
        resendConfirmation,
        passwordRecovery,
        // Called by ResetPassword after the user finishes setting a new
        // password. Clears the recovery flag so subsequent auth events
        // (fresh sign-in on /login) are handled normally again.
        finishPasswordRecovery: () => setPasswordRecovery(false),
        impersonateUser,
        setImpersonateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const roleLabel = (r) => ({
  super_admin: "Super Admin",
  admin: "Admin",
  team_leader: "Team Leader",
  tele_sales: "Tele Sales",
  hr: "HR",
  accounts: "Accounts",
}[r] || r);
